/**
 * A chip tint from one tag colour (#681, following #679's D2 rule). A tag has a
 * single hex; the chip needs the same {bg, ink, edge} triple the fixed TINTS
 * carry: a pale wash for the fill, a mid wash for the edge, and an ink darkened
 * until it reads on that fill at WCAG AA (4.5:1).
 */
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(value) {
    return typeof value === 'string' && HEX.test(value.trim());
}

function toRgb(hex) {
    let h = hex.trim().slice(1);
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
}

function toHex(rgb) {
    return `#${rgb.map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

function mix(rgb, target, amount) {
    return rgb.map((v, i) => v + (target[i] - v) * amount);
}

function luminance(rgb) {
    const [r, g, b] = rgb.map(v => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
    const [hi, lo] = [luminance(toRgb(a)), luminance(toRgb(b))].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

/** `{bg, ink, edge}` for a hex colour, or null for anything that is not one. */
export function tintFromHex(hex) {
    if (!isHexColor(hex)) return null;
    const rgb = toRgb(hex);
    const bg = toHex(mix(rgb, WHITE, 0.88));
    const edge = toHex(mix(rgb, WHITE, 0.7));
    let ink = toHex(rgb);
    for (let step = 0.1; contrastRatio(ink, bg) < 4.5 && step <= 1; step += 0.1) {
        ink = toHex(mix(rgb, BLACK, step));
    }
    return { bg, ink, edge };
}
